import { SxProps, TextField, Theme } from '@mui/material';
import { useState, useEffect } from 'react';
import { DATA_GRID_MEMORY_SEARCH_DEBOUNCE } from 'src/constants/memory';

export interface SearchBarProps {
  sx?: SxProps<Theme>;
  onSearch: (searchString: string) => void;
  onReset: () => void;
  initialValue?: string;
  onTextChange?: (value: string) => void;
  placeholder?: string;
  debounceDuration?: number;
}

export const SearchBar = ({
  sx,
  onSearch,
  onReset,
  initialValue = '',
  onTextChange,
  placeholder = 'Search memories',
  debounceDuration = DATA_GRID_MEMORY_SEARCH_DEBOUNCE,
}: SearchBarProps) => {
  const [searchText, setSearchText] = useState(initialValue);
  const [isUserSearched, setIsUserSearched] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  useEffect(() => {
    setSearchText(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, debounceDuration);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, debounceDuration]);

  useEffect(() => {
    if (debouncedSearchText.trim() !== '') {
      onSearch(debouncedSearchText);
      setIsUserSearched(true);
    } else if (isUserSearched) {
      onReset();
      setIsUserSearched(false);
    }
  }, [debouncedSearchText, onSearch, onReset]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchText(value);
    onTextChange?.(value);
  };

  return (
    <TextField
      onChange={handleChange}
      value={searchText}
      sx={sx}
      fullWidth
      placeholder={placeholder}
      variant="outlined"
    />
  );
};
